import type { AchievementId } from "../services/AchievementService";

// A SkinDef is a tiny material-config blob that Player.setMaterialConfig
// applies to the existing ball material in place. Keeping it minimal
// (color, emissive, emissiveIntensity, roughness, metalness) means we
// can swap skins without touching geometry, lights, or any of the
// physics setup.
//
// Skins are unlocked by satisfying their `requires` achievement (or are
// available from the start when `requires` is null). The default skin
// has id "default" and is always unlocked.

export interface SkinDef {
  id: string;
  name: string;
  // Player ball base color.
  color: number;
  emissive: number;
  emissiveIntensity: number;
  roughness: number;
  metalness: number;
  // null -> always unlocked. Otherwise the matching AchievementId must
  // be in AchievementService.getUnlockedSet() before the skin shows up
  // as selectable in the cosmetics list.
  requires: AchievementId | null;
}

export const SKINS: SkinDef[] = [
  {
    id: "default",
    name: "Pristine",
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 0.5,
    roughness: 0.2,
    metalness: 0.0,
    requires: null,
  },
  {
    id: "neon_cyan",
    name: "Neon Cyan",
    color: 0x2bd3ff,
    emissive: 0x2bd3ff,
    emissiveIntensity: 0.85,
    roughness: 0.18,
    metalness: 0.05,
    requires: "first_run",
  },
  {
    id: "molten",
    name: "Molten",
    color: 0xff7a3c,
    emissive: 0xff3010,
    emissiveIntensity: 1.05,
    roughness: 0.32,
    metalness: 0.0,
    requires: "boss_first",
  },
  {
    id: "phantom",
    name: "Phantom",
    color: 0x6e3aff,
    emissive: 0xff5cb1,
    emissiveIntensity: 0.95,
    roughness: 0.22,
    metalness: 0.0,
    requires: "save_first",
  },
  {
    id: "gilded",
    name: "Gilded",
    color: 0xffd23f,
    emissive: 0xffba6b,
    emissiveIntensity: 0.85,
    roughness: 0.16,
    metalness: 0.55,
    requires: "combo_50",
  },
  {
    id: "obsidian",
    name: "Obsidian",
    color: 0x080812,
    emissive: 0x6e3aff,
    emissiveIntensity: 1.2,
    roughness: 0.4,
    metalness: 0.7,
    requires: "boss_all_four",
  },
];

export const DEFAULT_SKIN_ID = "default";

export function skinById(id: string): SkinDef {
  return SKINS.find((s) => s.id === id) ?? SKINS[0]!;
}
